import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  cardTitleClass,
  btnPrimaryClass,
  btnSecondaryClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  formGridClass,
  formFullWidthClass,
  alertErrorClass,
  badgeClass,
  btnIconEditClass,
  btnIconDeleteClass,
  btnIconConfirmClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
  tableActionsCellClass,
  spinnerClass,
} from "../dashboardStyles";
import {
  GetBookingsAction,
  GetResourcesAction,
  GetServicesAction,
  CreateBookingAction,
  UpdateBookingAction,
  UpdateBookingStatusAction,
  DeleteBookingAction,
  GetAvailableTimeSlotsAuthAction,
  getUserInfoFromToken,
} from "../../../store/action";
import { UserRole } from "../../../constants/roles";
import { serviceMinutes } from "../../../utils/serviceDuration";
import {
  formatSlotLabel,
  formatWallClockDateTime,
  toSlotString,
  wallClockISODate,
} from "../../../utils/dates";
import { notify, notifySaveError } from "../../../utils/notify";
import { BookingStatusNoticeKeys } from "../../../constants/enums";

/**
 * Bookings Management Page Component
 * Allows users to view, create, edit, and manage bookings
 * CompanyAdmin can see all bookings for their organization
 */
const Bookings = () => {
  const { t } = useTranslation();
  // State for bookings list
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  // State for filters
  const [filters, setFilters] = useState({
    status: "",
    resourceId: "",
    serviceId: "",
    startDate: "",
    endDate: "",
    searchTerm: "",
    sortBy: "BookingDateTime",
    sortDescending: true,
  });

  // State for resources and services (for dropdowns)
  const [resources, setResources] = useState([]);
  const [services, setServices] = useState([]);

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [modalMode, setModalMode] = useState("create"); // 'create', 'edit', 'view'

  // State for form data
  const [formData, setFormData] = useState({
    serviceId: "",
    resourceId: "",
    bookingDate: "",
    timeSlot: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    notes: "",
    status: "Pending",
  });

  // State for available time slots
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // State for saving
  const [saving, setSaving] = useState(false);

  // User info
  const [userInfo, setUserInfo] = useState(null);

  // Initialize user info
  useEffect(() => {
    const info = getUserInfoFromToken();
    setUserInfo(info);
  }, []);

  // Fetch resources and services on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [resourcesData, servicesData] = await Promise.all([
          GetResourcesAction(),
          GetServicesAction(),
        ]);
        setResources(resourcesData);
        setServices(servicesData);
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const result = await GetBookingsAction({
        ...filters,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setBookings(result.bookings || []);
      setPagination((prev) => ({
        ...prev,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
      }));
      setError(null);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(t("Dashboard.Bookings.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Fetch available time slots when resource, service, and date change
  useEffect(() => {
    const fetchSlots = async () => {
      if (formData.resourceId && formData.serviceId && formData.bookingDate) {
        setLoadingSlots(true);
        try {
          const slots = await GetAvailableTimeSlotsAuthAction(
            formData.resourceId,
            formData.serviceId,
            formData.bookingDate,
            // A booking must not block its own slot while being edited, or its
            // current time is the one time the form cannot offer back.
            modalMode === "edit" ? editingBooking?.id : null,
          );
          setAvailableSlots(slots);
        } catch (err) {
          console.error("Error fetching time slots:", err);
          setAvailableSlots([]);
        } finally {
          setLoadingSlots(false);
        }
      } else {
        setAvailableSlots([]);
      }
    };
    fetchSlots();
  }, [
    formData.resourceId,
    formData.serviceId,
    formData.bookingDate,
    modalMode,
    editingBooking?.id,
  ]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: "",
      resourceId: "",
      serviceId: "",
      startDate: "",
      endDate: "",
      searchTerm: "",
      sortBy: "BookingDateTime",
      sortDescending: true,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Open modal for creating
  const handleCreate = () => {
    setEditingBooking(null);
    setModalMode("create");
    setFormData({
      serviceId: "",
      resourceId: "",
      bookingDate: "",
      timeSlot: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      notes: "",
      status: "Pending",
    });
    setAvailableSlots([]);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (booking) => {
    setEditingBooking(booking);
    setModalMode("edit");
    setFormData({
      serviceId: booking.serviceId?.toString() || "",
      resourceId: booking.resourceId?.toString() || "",
      // Wall clock, not an instant: the stored value carries a `Z` it does not
      // mean, and the slot dropdown is keyed by the API's own slot strings.
      bookingDate: wallClockISODate(booking.bookingDateTime),
      timeSlot: toSlotString(booking.bookingDateTime),
      customerName: booking.customerName || "",
      customerEmail: booking.customerEmail || "",
      customerPhone: booking.customerPhone || "",
      notes: booking.notes || "",
      status: booking.status || "Pending",
    });
    setShowModal(true);
  };

  // Open modal for viewing
  const handleView = (booking) => {
    setEditingBooking(booking);
    setModalMode("view");
    setFormData({
      serviceId: booking.serviceId?.toString() || "",
      resourceId: booking.resourceId?.toString() || "",
      // Wall clock, not an instant: the stored value carries a `Z` it does not
      // mean, and the slot dropdown is keyed by the API's own slot strings.
      bookingDate: wallClockISODate(booking.bookingDateTime),
      timeSlot: toSlotString(booking.bookingDateTime),
      customerName: booking.customerName || "",
      customerEmail: booking.customerEmail || "",
      customerPhone: booking.customerPhone || "",
      notes: booking.notes || "",
      status: booking.status || "Pending",
    });
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBooking(null);
  };

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (modalMode === "create") {
        await CreateBookingAction({
          serviceId: parseInt(formData.serviceId, 10),
          resourceId: parseInt(formData.resourceId, 10),
          bookingDate: formData.bookingDate,
          timeSlot: formData.timeSlot,
          customerName: formData.customerName,
          // Optional for staff. Null, never "": the API's email check passes
          // an absent address and rejects an empty one.
          customerEmail: formData.customerEmail.trim() || null,
          customerPhone: formData.customerPhone || null,
          notes: formData.notes || null,
        });
      } else if (modalMode === "edit") {
        await UpdateBookingAction(editingBooking.id, {
          serviceId: parseInt(formData.serviceId, 10),
          resourceId: parseInt(formData.resourceId, 10),
          bookingDate: formData.bookingDate,
          timeSlot: formData.timeSlot,
          customerName: formData.customerName,
          // Null leaves the stored address as it is — and a booking taken by
          // name alone has none to send back.
          customerEmail: formData.customerEmail.trim() || null,
          customerPhone: formData.customerPhone || null,
          notes: formData.notes || null,
          status: formData.status,
        });
      }

      await fetchBookings();
      handleCloseModal();
      notify.success(
        t(
          modalMode === "create"
            ? "Dashboard.Bookings.notices.created"
            : "Dashboard.Bookings.notices.updated",
        ),
      );
    } catch (err) {
      console.error("Error saving booking:", err);
      notifySaveError(err, t("Dashboard.Bookings.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await UpdateBookingStatusAction(bookingId, newStatus);
      notify.success(
        t(BookingStatusNoticeKeys[newStatus] ?? "Dashboard.Bookings.notices.updated"),
      );
      await fetchBookings();
    } catch (err) {
      console.error("Error updating status:", err);
      notifySaveError(err, t("Dashboard.Bookings.errors.statusUpdateFailed"));
    }
  };

  // Handle delete
  const handleDelete = async (bookingId) => {
    if (
      !window.confirm(t("Dashboard.Bookings.confirm.deleteBooking"))
    ) {
      return;
    }

    try {
      await DeleteBookingAction(bookingId);
      notify.success(t("Dashboard.Bookings.notices.deleted"));
      await fetchBookings();
    } catch (err) {
      console.error("Error deleting booking:", err);
      notifySaveError(err, t("Dashboard.Bookings.errors.deleteFailed"));
    }
  };

  // Format date for display. Wall clock — see utils/dates.js.
  const formatDateTime = (dateString) => formatWallClockDateTime(dateString);

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "bg-brand-50 text-brand-700";
      case "pending":
        return "bg-amber-100 text-amber-800";
      case "cancelled":
        return "bg-red-50 text-red-700";
      case "completed":
        return "bg-sky-100 text-sky-800";
      default:
        return "bg-ink-100 text-ink-600";
    }
  };

  // Check if user is admin
  const isAdmin =
    userInfo &&
    (userInfo.role === UserRole.SYSTEM_OWNER ||
      userInfo.role === UserRole.ADMIN ||
      userInfo.role === UserRole.COMPANY_ADMIN);

  if (loading && bookings.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div role="status" aria-label={t("Dashboard.Bookings.loading")} className={spinnerClass} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitleClass}>{t("Dashboard.Bookings.title")}</h1>
          <p className={pageSubtitleClass}>
            {isAdmin
              ? t("Dashboard.Bookings.subtitleAdmin")
              : t("Dashboard.Bookings.subtitle")}
          </p>
        </div>
        <button
          className={`${btnPrimaryClass} shrink-0`}
          onClick={handleCreate}
        >
          <i className="bi bi-plus-lg" />
          {t("Dashboard.Bookings.newBooking")}
        </button>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            className="shrink-0 cursor-pointer rounded p-0.5 text-red-700 transition hover:bg-red-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            onClick={() => setError(null)}
            aria-label={t("Dashboard.Bookings.dismissError")}
          >
            <i className="bi bi-x text-lg" />
          </button>
        </div>
      )}

      {/* Filters Section */}
      <div className={`${cardClass} mb-5`}>
        <div className={cardBodyClass}>
          <form onSubmit={handleSearch}>
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(200px,100%),1fr))]">
              <div>
                <label htmlFor="searchTerm" className={formLabelClass}>
                  {t("Dashboard.Bookings.filters.search")}
                </label>
                <input
                  type="text"
                  id="searchTerm"
                  name="searchTerm"
                  value={filters.searchTerm}
                  onChange={handleFilterChange}
                  placeholder={t("Dashboard.Bookings.filters.searchPlaceholder")}
                  className={formControlClass}
                />
              </div>

              <div>
                <label htmlFor="status" className={formLabelClass}>
                  {t("Dashboard.Bookings.filters.status")}
                </label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className={formControlClass}
                >
                  <option value="">{t("Dashboard.Bookings.filters.allStatuses")}</option>
                  <option value="Pending">{t("Dashboard.Bookings.status.pending")}</option>
                  <option value="Confirmed">{t("Dashboard.Bookings.status.confirmed")}</option>
                  <option value="Completed">{t("Dashboard.Bookings.status.completed")}</option>
                  <option value="Cancelled">{t("Dashboard.Bookings.status.cancelled")}</option>
                </select>
              </div>

              <div>
                <label htmlFor="resourceId" className={formLabelClass}>
                  {t("Dashboard.Bookings.filters.resource")}
                </label>
                <select
                  id="resourceId"
                  name="resourceId"
                  value={filters.resourceId}
                  onChange={handleFilterChange}
                  className={formControlClass}
                >
                  <option value="">{t("Dashboard.Bookings.filters.allResources")}</option>
                  {resources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="serviceId" className={formLabelClass}>
                  {t("Dashboard.Bookings.filters.service")}
                </label>
                <select
                  id="serviceId"
                  name="serviceId"
                  value={filters.serviceId}
                  onChange={handleFilterChange}
                  className={formControlClass}
                >
                  <option value="">{t("Dashboard.Bookings.filters.allServices")}</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="startDate" className={formLabelClass}>
                  {t("Dashboard.Bookings.filters.fromDate")}
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className={formControlClass}
                />
              </div>

              <div>
                <label htmlFor="endDate" className={formLabelClass}>
                  {t("Dashboard.Bookings.filters.toDate")}
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className={formControlClass}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-ink-200 pt-4">
              <button type="submit" className={btnPrimaryClass}>
                <i className="bi bi-search" />
                {t("Dashboard.Bookings.filters.searchButton")}
              </button>
              <button
                type="button"
                className={btnSecondaryClass}
                onClick={resetFilters}
              >
                <i className="bi bi-x-circle" />
                {t("Dashboard.Bookings.filters.reset")}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Bookings Table */}
      <div className={cardClass}>
        <div className={cardBodyClass}>
          {bookings.length === 0 ? (
            <div className="flex flex-col items-center px-4 py-12 text-center">
              <div className="mb-5 flex size-20 items-center justify-center rounded-full bg-brand-50">
                <i className="bi bi-calendar-check text-4xl text-brand-600" />
              </div>
              <h2 className="m-0 text-xl font-semibold text-ink-900">
                {t("Dashboard.Bookings.emptyState.title")}
              </h2>
              <p className="mt-2 mb-6 max-w-md text-fluid-sm text-ink-500">
                {filters.searchTerm ||
                filters.status ||
                filters.resourceId ||
                filters.serviceId
                  ? t("Dashboard.Bookings.emptyState.noMatches")
                  : t("Dashboard.Bookings.emptyState.noBookingsYet")}
              </p>
              <button className={btnPrimaryClass} onClick={handleCreate}>
                <i className="bi bi-plus-lg" />
                {t("Dashboard.Bookings.emptyState.createFirst")}
              </button>
            </div>
          ) : (
            <>
              <table className={tableClass}>
                <thead className={tableHeadClass}>
                  <tr>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.code")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.customer")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.service")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.resource")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.dateTime")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.status")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Bookings.table.actions")}</th>
                  </tr>
                </thead>
                <tbody className={tableBodyClass}>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className={tableRowClass}>
                      <td data-label={t("Dashboard.Bookings.table.code")} className={tableCellClass}>
                        <span className="rounded-md bg-ink-100 px-2 py-1 font-mono text-xs font-semibold text-ink-700">
                          {booking.confirmationCode}
                        </span>
                        {/* Only the widget is marked: it is the one source a
                            business wants to count, to see what its own site
                            brings in. */}
                        {booking.source === "Widget" && (
                          <span
                            className="ml-1.5 rounded-md bg-sky-100 px-2 py-1 text-xs font-medium text-sky-800"
                            title={t("Dashboard.Bookings.source.widgetTitle")}
                          >
                            {t("Dashboard.Bookings.source.widget")}
                          </span>
                        )}
                      </td>
                      <td data-label={t("Dashboard.Bookings.table.customer")} className={tableCellClass}>
                        <span className="flex flex-col max-md:items-end">
                          <span className="font-medium text-ink-900">
                            {booking.customerName}
                          </span>
                          {/* A walk-in may have been booked by name alone. */}
                          <span className="text-xs text-ink-500">
                            {booking.customerEmail || booking.customerPhone}
                          </span>
                        </span>
                      </td>
                      <td data-label={t("Dashboard.Bookings.table.service")} className={tableCellClass}>
                        {booking.serviceName}
                      </td>
                      <td data-label={t("Dashboard.Bookings.table.resource")} className={tableCellClass}>
                        {booking.resourceName}
                      </td>
                      <td data-label={t("Dashboard.Bookings.table.dateTime")} className={tableCellClass}>
                        {formatDateTime(booking.bookingDateTime)}
                      </td>
                      <td data-label={t("Dashboard.Bookings.table.status")} className={tableCellClass}>
                        <span
                          className={`${badgeClass} ${getStatusBadgeClass(booking.status)}`}
                        >
                          {booking.status}
                        </span>
                      </td>
                      <td data-label={t("Dashboard.Bookings.table.actions")} className={tableActionsCellClass}>
                        <span className="flex flex-wrap gap-1.5">
                          <button
                            className={btnIconEditClass}
                            aria-label={t("Dashboard.Bookings.actions.view", { code: booking.confirmationCode })}
                            onClick={() => handleView(booking)}
                          >
                            <i className="bi bi-eye" />
                          </button>
                          <button
                            className={btnIconEditClass}
                            aria-label={t("Dashboard.Bookings.actions.edit", { code: booking.confirmationCode })}
                            onClick={() => handleEdit(booking)}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          {booking.status === "Pending" && (
                            <button
                              className={btnIconConfirmClass}
                              aria-label={t("Dashboard.Bookings.actions.confirm", { code: booking.confirmationCode })}
                              onClick={() =>
                                handleStatusChange(booking.id, "Confirmed")
                              }
                            >
                              <i className="bi bi-check-lg" />
                            </button>
                          )}
                          {(booking.status === "Pending" ||
                            booking.status === "Confirmed") && (
                            <button
                              className={btnIconDeleteClass}
                              aria-label={t("Dashboard.Bookings.actions.cancel", { code: booking.confirmationCode })}
                              onClick={() =>
                                handleStatusChange(booking.id, "Cancelled")
                              }
                            >
                              <i className="bi bi-x-lg" />
                            </button>
                          )}
                          {booking.status === "Confirmed" && (
                            <button
                              className={btnIconConfirmClass}
                              aria-label={t("Dashboard.Bookings.actions.complete", { code: booking.confirmationCode })}
                              onClick={() =>
                                handleStatusChange(booking.id, "Completed")
                              }
                            >
                              <i className="bi bi-check-circle" />
                            </button>
                          )}
                          <button
                            className={btnIconDeleteClass}
                            aria-label={t("Dashboard.Bookings.actions.delete", { code: booking.confirmationCode })}
                            onClick={() => handleDelete(booking.id)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3 border-t border-ink-200 pt-4">
                  <button
                    className={btnSecondaryClass}
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    aria-label={t("Dashboard.Bookings.pagination.previousPage")}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  <span className="text-fluid-sm text-ink-500">
                    {t("Dashboard.Bookings.pagination.pageOf", {
                      page: pagination.page,
                      totalPages: pagination.totalPages,
                      totalCount: pagination.totalCount,
                    })}
                  </span>
                  <button
                    className={btnSecondaryClass}
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    aria-label={t("Dashboard.Bookings.pagination.nextPage")}
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className={modalOverlayClass}
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
        >
          <div className={modalPanelClass} onClick={(e) => e.stopPropagation()}>
            <div className={modalHeaderClass}>
              <h2 id="booking-modal-title" className={cardTitleClass}>
                {modalMode === "create"
                  ? t("Dashboard.Bookings.modal.newBooking")
                  : modalMode === "edit"
                    ? t("Dashboard.Bookings.modal.editBooking")
                    : t("Dashboard.Bookings.modal.bookingDetails")}
              </h2>
              <button
                className={modalCloseClass}
                onClick={handleCloseModal}
                aria-label={t("Dashboard.Bookings.modal.close")}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className={modalBodyClass}>
                {modalMode === "view" && editingBooking && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-ink-50 px-4 py-3">
                    <span className="text-xs font-semibold text-ink-600">
                      {t("Dashboard.Bookings.modal.confirmationCode")}
                    </span>
                    <span className="rounded-md bg-white px-2 py-1 font-mono text-xs font-semibold text-ink-900">
                      {editingBooking.confirmationCode}
                    </span>
                  </div>
                )}

                <div className={formGridClass}>
                  <div>
                    <label htmlFor="resourceId-modal" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.resource")} <span className={requiredMarkClass}>*</span>
                    </label>
                    <select
                      id="resourceId-modal"
                      name="resourceId"
                      value={formData.resourceId}
                      onChange={handleFormChange}
                      className={formControlClass}
                      required
                      disabled={modalMode === "view"}
                    >
                      <option value="">{t("Dashboard.Bookings.form.selectResource")}</option>
                      {resources.map((resource) => (
                        <option key={resource.id} value={resource.id}>
                          {resource.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="serviceId-modal" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.service")} <span className={requiredMarkClass}>*</span>
                    </label>
                    <select
                      id="serviceId-modal"
                      name="serviceId"
                      value={formData.serviceId}
                      onChange={handleFormChange}
                      className={formControlClass}
                      required
                      disabled={modalMode === "view"}
                    >
                      <option value="">{t("Dashboard.Bookings.form.selectService")}</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {`${service.name} (${t("Dashboard.Bookings.form.minutes", {
                            count: serviceMinutes(service),
                          })})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="bookingDate" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.date")} <span className={requiredMarkClass}>*</span>
                    </label>
                    <input
                      type="date"
                      id="bookingDate"
                      name="bookingDate"
                      value={formData.bookingDate}
                      onChange={handleFormChange}
                      className={formControlClass}
                      required
                      disabled={modalMode === "view"}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>

                  <div>
                    <label htmlFor="timeSlot" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.time")} <span className={requiredMarkClass}>*</span>
                    </label>
                    {modalMode === "view" ? (
                      <input
                        type="text"
                        id="timeSlot"
                        value={formData.timeSlot}
                        className={formControlClass}
                        disabled
                      />
                    ) : (
                      <select
                        id="timeSlot"
                        name="timeSlot"
                        value={formData.timeSlot}
                        onChange={handleFormChange}
                        className={formControlClass}
                        required
                        disabled={
                          !formData.resourceId ||
                          !formData.serviceId ||
                          !formData.bookingDate
                        }
                      >
                        <option value="">
                          {loadingSlots
                            ? t("Dashboard.Bookings.form.loading")
                            : availableSlots.length === 0
                              ? t("Dashboard.Bookings.form.selectResourceServiceDateFirst")
                              : t("Dashboard.Bookings.form.selectTime")}
                        </option>
                        {availableSlots.map((slot) => (
                          <option key={slot} value={slot}>
                            {formatSlotLabel(slot)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label htmlFor="customerName" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.customerName")} <span className={requiredMarkClass}>*</span>
                    </label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleFormChange}
                      className={formControlClass}
                      required
                      disabled={modalMode === "view"}
                      placeholder={t("Dashboard.Bookings.form.customerNamePlaceholder")}
                    />
                  </div>

                  <div>
                    <label htmlFor="customerEmail" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.customerEmail")}
                    </label>
                    <input
                      type="email"
                      id="customerEmail"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleFormChange}
                      className={formControlClass}
                      disabled={modalMode === "view"}
                      placeholder={t("Dashboard.Bookings.form.customerEmailPlaceholder")}
                    />
                  </div>

                  <div>
                    <label htmlFor="customerPhone" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.customerPhone")}
                    </label>
                    <input
                      type="tel"
                      id="customerPhone"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleFormChange}
                      className={formControlClass}
                      disabled={modalMode === "view"}
                      placeholder={t("Dashboard.Bookings.form.customerPhonePlaceholder")}
                    />
                  </div>

                  {modalMode === "edit" && (
                    <div>
                      <label htmlFor="status-modal" className={formLabelClass}>
                        {t("Dashboard.Bookings.form.status")}
                      </label>
                      <select
                        id="status-modal"
                        name="status"
                        value={formData.status}
                        onChange={handleFormChange}
                        className={formControlClass}
                      >
                        <option value="Pending">{t("Dashboard.Bookings.status.pending")}</option>
                        <option value="Confirmed">{t("Dashboard.Bookings.status.confirmed")}</option>
                        <option value="Completed">{t("Dashboard.Bookings.status.completed")}</option>
                        <option value="Cancelled">{t("Dashboard.Bookings.status.cancelled")}</option>
                      </select>
                    </div>
                  )}

                  <div className={formFullWidthClass}>
                    <label htmlFor="notes" className={formLabelClass}>
                      {t("Dashboard.Bookings.form.notes")}
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleFormChange}
                      className={formControlClass}
                      rows="3"
                      disabled={modalMode === "view"}
                      placeholder={t("Dashboard.Bookings.form.notesPlaceholder")}
                    />
                  </div>
                </div>

                {modalMode === "view" && editingBooking && (
                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-ink-200 pt-3 text-xs text-ink-500">
                    <span>
                      {t("Dashboard.Bookings.modal.created")}{" "}
                      {new Date(editingBooking.createdAt).toLocaleString()}
                    </span>
                    <span>
                      {t("Dashboard.Bookings.modal.duration", {
                        duration: editingBooking.duration,
                      })}
                    </span>
                  </div>
                )}
              </div>

              <div className={modalFooterClass}>
                <button
                  type="button"
                  className={btnSecondaryClass}
                  onClick={handleCloseModal}
                >
                  {modalMode === "view"
                    ? t("Dashboard.Bookings.modal.closeButton")
                    : t("Dashboard.Bookings.modal.cancelButton")}
                </button>
                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className={btnPrimaryClass}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none" />
                        {t("Dashboard.Bookings.modal.saving")}
                      </>
                    ) : modalMode === "create" ? (
                      t("Dashboard.Bookings.modal.createBookingButton")
                    ) : (
                      t("Dashboard.Bookings.modal.saveChangesButton")
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
